import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Boxes } from "lucide-react";

const TIERS = [
  { key: "top_budget", label: "TOP BUDGET", range: "100 – 290 €/an" },
  { key: "medium_budget", label: "MEDIUM BUDGET", range: "290 – 790 €/an" },
  { key: "standard_budget", label: "STANDARD BUDGET", range: "790 – 990 €/an" },
  { key: "maximum_budget", label: "MAXIMUM BUDGET", range: "1000 €+ /an" },
];

const CATEGORY_LABELS = {
  commerce: "Commerce",
  rh: "RH & Paie",
  finance: "Finance",
  web: "Web",
  tech: "Technique",
  addons: "Add-ons",
  secteur: "Modules métier",
};

/**
 * Panel Constructeur d'offre (Phase 11 v2.0.01).
 * L'Admin/Commercial coche les modules à activer + choisit la formule + saisit le prix négocié.
 */
export default function OfferBuilderPanel({ entrepriseId, entreprise, onSaved }) {
  const [catalog, setCatalog] = useState([]);
  const [current, setCurrent] = useState(null);
  const [tier, setTier] = useState("medium_budget");
  const [selected, setSelected] = useState(new Set());
  const [monthlyPrice, setMonthlyPrice] = useState("");
  const [annualPrice, setAnnualPrice] = useState("");
  const [cycle, setCycle] = useState("annual");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, o] = await Promise.all([
          api.get("/admin/modules-catalog"),
          api.get(`/admin/entreprises/${entrepriseId}/offer`),
        ]);
        setCatalog(c.data.modules || []);
        setCurrent(o.data || null);
        if (o.data && o.data.tier) {
          setTier(o.data.tier);
          setSelected(new Set(o.data.modules || []));
          setMonthlyPrice(o.data.monthly_price ?? "");
          setAnnualPrice(o.data.annual_price ?? "");
          setCycle(o.data.cycle || "annual");
          setNotes(o.data.notes || "");
        }
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [entrepriseId]);

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/entreprises/${entrepriseId}/offer`, {
        tier,
        modules: Array.from(selected),
        monthly_price: monthlyPrice ? parseFloat(monthlyPrice) : null,
        annual_price: annualPrice ? parseFloat(annualPrice) : null,
        cycle,
        notes: notes || null,
      });
      toast.success("Offre enregistrée");
      if (onSaved) onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const grouped = catalog.reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="offer-builder">
      <div className="bg-white border border-[#DEE2E6] p-6">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-3 flex items-center gap-2">
          <Boxes className="h-3.5 w-3.5" /> Formule PAG v2.0.01
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {TIERS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTier(t.key)}
              data-testid={`tier-${t.key}`}
              className={`p-3 border-2 text-left transition-colors ${tier === t.key ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}
            >
              <div className="text-xs font-bold">{t.label}</div>
              <div className="text-[10px] text-[#868E96] mt-0.5">{t.range}</div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Prix mensuel (€)</Label>
            <Input type="number" step="0.01" value={monthlyPrice} onChange={(e) => setMonthlyPrice(e.target.value)} data-testid="offer-monthly" className="h-10" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Prix annuel (€)</Label>
            <Input type="number" step="0.01" value={annualPrice} onChange={(e) => setAnnualPrice(e.target.value)} data-testid="offer-annual" className="h-10" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Cycle facturation</Label>
            <Select value={cycle} onValueChange={setCycle}>
              <SelectTrigger data-testid="offer-cycle" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Mensuel</SelectItem>
                <SelectItem value="annual">Annuel</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex flex-col justify-end">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#868E96]">Modules cochés</div>
            <div className="font-display text-2xl font-black text-[#002FA7]">{selected.size}<span className="text-sm text-[#868E96]"> / {catalog.length}</span></div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#DEE2E6] p-6">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-4">Modules à activer</div>
        <div className="text-xs text-[#495057] mb-4 p-3 bg-[#FFFBEB] border-l-4 border-[#FFD700]">
          ⚠️ Cette configuration ne <b>supprime aucun module existant</b> ni aucune donnée. Elle définit uniquement les accès activés dans l&apos;interface du chef.
        </div>
        {Object.entries(grouped).map(([cat, mods]) => (
          <div key={cat} className="mb-6" data-testid={`module-group-${cat}`}>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0A0A0A] border-b border-[#0A0A0A] pb-1 mb-3">
              {CATEGORY_LABELS[cat] || cat}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {mods.map((m) => (
                <label
                  key={m.key}
                  data-testid={`module-${m.key}`}
                  className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${selected.has(m.key) ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(m.key)}
                    onChange={() => toggle(m.key)}
                    className="h-4 w-4 accent-[#002FA7]"
                    data-testid={`module-cb-${m.key}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{m.name}</div>
                    <div className="text-[10px] text-[#868E96] font-mono">{m.key}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        ))}

        <div className="mt-4 space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Notes internes commercial</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="offer-notes" placeholder="Ex : Négociation à -10%, contrat 2 ans…" />
        </div>

        <Button onClick={save} disabled={saving} data-testid="offer-save" className="mt-4 h-11 bg-[#002FA7] text-white">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer l'offre"}
        </Button>
      </div>
    </div>
  );
}
